import axios from "axios"

import { StageType } from "@models/common"
import FileSaver from "file-saver"
import qs from "qs"
import { CardColor } from "src/components/browser/card/BrowserCard"
import { UploadedJobFile } from "src/sites/file/droparea/expiringFiles"
import UserService from "src/UserService"
import { HTTPLogger } from "src/utils/logger/HTTPLogger"
export type JobType = "COMMERCIAL" | "EXAMINATION" | "CHARITY"

export interface PageResponse<T> {
  totalCount: number
  results: T[]
}

export interface SimpleExecutorRecord {
  id: string
  firstName: string
  lastName: string
  userName: string
}

export type ExecutorSelectionStageParameter = "manual" | "auto" | "insourcing"

export interface StageInput {
  stage: StageType
  executorSelection?: ExecutorSelectionStageParameter
  preselectedExecutorId?: string
}

export interface AssetVisibility {
  open: boolean
  communities: string[]
}

export type PricingType = "FIXED" | "DYNAMIC" | "SUBSCRIPTION" | "FIXED_ASSET_BASED"

export const getPossibleInsourceExecutors = (communityIds: string[]) =>
  axios
    .get<SimpleExecutorRecord[]>("/api/wfl/portal/possible-insource-executors", {
      params: { communityIds: communityIds },
      paramsSerializer: (params) => {
        return qs.stringify(params, { arrayFormat: "repeat" })
      }
    })
    .then((r) => r.data)

export enum MultiLangJobStatus {
  IN_PROGRESS = "IN_PROGRESS",
  FINISHED = "FINISHED",
  CANCELLED = "CANCELLED",
  QUOTED = "QUOTED",
  QUOTING = "QUOTING"
}

interface MultilangJobInputFile {
  fileId: string
  name: string
}

export interface MultilangJobCreationRequest {
  inputFilesWithName: MultilangJobInputFile[]
  referenceFiles: string[]
  type: JobType
  genre: string
  langFrom: string
  langsTo: string[]
  taxonomy: string[]
  visibility: AssetVisibility
  stages: StageInput[]
  externalProjectId?: string
  requesterNote?: string
  name: string
  pricingType: PricingType
  quoting: boolean
  tabletName?: string
  generateTcert?: boolean
  pricingRuleId?: string
}

export async function createMultilangJob(job: MultilangJobCreationRequest) {
  // Normalize language codes to lowercase
  const normalizedJob = {
    ...job,
    langFrom: job.langFrom.toLowerCase(),
    langsTo: job.langsTo.map((lang) => lang.toLowerCase())
  }
  await axios.post(`/api/snc/multilang-jobs`, normalizedJob)
}

export interface MultilangJobFilters {
  statuses?: string
  beforeQuoteDecision?: boolean
  searchQuery?: string
  uploadDateFrom?: string
  uploadDateTo?: string
  requesterId?: string
}

export interface MultilangJobPage {
  offset: number
  limit: number
  sortBy?: string
  sortDirection?: string
}

export interface MultilangJobParams extends MultilangJobFilters, MultilangJobPage {}

export const getJobName = (files: UploadedJobFile[]) => {
  switch (files.length) {
    case 1:
      return files[0].name
    case 2:
      return `${files[0].name} and ${files.length - 1} more file`
    default:
      return `${files[0].name} and ${files.length - 1} more files`
  }
}

type SingleLangJobStatus = "IN_PROGRESS" | "FINISHED" | "CANCELLED"

export interface SingleLangJob {
  jobId: string
  targetLanguage: string
  resultFilesReady: boolean
  status: SingleLangJobStatus
  canReopen: boolean
  currentStage?: string
  newAssetsWordCount?: number
  repeatedAssetsWordCount?: number
  recycledAssetsWordCount?: number
}

export interface MultilangJob {
  id: string
  uploadDate: string
  deliveryDate?: string
  dueDate?: string
  genre: string
  taxonomy: string[]
  gatedCommunityIds: string[]
  projectName: string
  tabletName: string
  singleLangJobs: SingleLangJob[]
  openableAssignmentId?: string
  resultFilesReady: boolean
  status: MultiLangJobStatus
  quotedTotalCost?: number
  pricingType: PricingType
  externalProjectId?: string
  langFrom: string
  requesterId: string
  newAssetsWordCount?: number
  repeatedAssetsWordCount?: number
  recycledAssetsWordCount?: number
  cost?: number
  currency?: string
}
export type MultilangJobSortBy = "name" | "uploadDate" | "status" | "deliveryDate"

export async function getMultilangJobs(params: MultilangJobParams): Promise<PageResponse<MultilangJob>> {
  const resp = await axios.get("/api/snc/multilang-jobs", { params })
  const { totalCount, results } = resp.data
  return { totalCount, results }
}

export const acceptJobQuote = (multilangJobId: string) =>
  axios.put(`/api/snc/multilang-jobs/${multilangJobId}/acceptQuote`)

export const declineQuote = (multilangJobId: string) =>
  axios.put(`/api/snc/multilang-jobs/${multilangJobId}/declineQuote`)

export const updateExternalProjectId = (multilangJobId: string, externalProjectId: string) =>
  axios.put(`/api/snc/multilang-jobs/${multilangJobId}/external-project-id`, { externalProjectId })

export const displayedName = (multilangJob: MultilangJob) => multilangJob.externalProjectId || multilangJob.projectName

export interface Comment {
  content: string
  date: string
}

export interface ReferenceFile {
  id: string
  name: string
}

export const getComments = (multilangJobId: string, singleLangJobId: string) =>
  axios
    .get(`/api/snc/multilang-jobs/${multilangJobId}/singleLangJobs/${singleLangJobId}/comments`)
    .then((response) => response.data)

export const putComment = (multilangJobId: string, singleLangJobId: string, comment: string) =>
  axios.post(`/api/snc/multilang-jobs/${multilangJobId}/singleLangJobs/${singleLangJobId}/comments`, comment)

export const getReferenceFiles = (multilangJobId: string, singleLangJobId: string) =>
  axios
    .get<ReferenceFile[]>(`/api/snc/multilang-jobs/${multilangJobId}/singleLangJobs/${singleLangJobId}/files`)
    .then((response) => response.data)

export const putReferenceFiles = (multilangJobId: string, singleLangJobId: string, fileIds: string[]) =>
  axios.put(`/api/snc/multilang-jobs/${multilangJobId}/singleLangJobs/${singleLangJobId}/files`, fileIds)

export const reopenSingleLangJob = (multilangJobId: string, singleLangJobId: string) =>
  axios.post(`/api/snc/multilang-jobs/${multilangJobId}/singleLangJobs/${singleLangJobId}/reopen`)

// Type declaration for React Native WebView
declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: string) => void
    }
  }
}

export async function downloadFile(url: string, fileName: string) {
  // Check if running in React Native WebView
  if (window.ReactNativeWebView) {
    const logger = new HTTPLogger() //todo remove this
    const token = await UserService.getToken()
    window.ReactNativeWebView.postMessage(
      JSON.stringify({
        type: "DOWNLOAD_URL_AUTH",
        url,
        fileName,
        accessToken: token
      })
    )
    logger.info(`Downloading file ${fileName} from ${url}`)
  } else {
    const resp = await axios.get(url, { responseType: "blob" })
    const contentType = resp.headers["content-type"]
    FileSaver.saveAs(new Blob([resp.data], { type: contentType }), fileName)
  }
}

async function downloadZipFile(job: MultilangJob) {
  const url = `/api/snc/multilang-jobs/${job.id}/downloadAll`
  const fileName = `${displayedName(job)}.zip`
  return downloadFile(url, fileName)
}

export async function downloadSingleJobFiles(multilangJob: MultilangJob, singleLangJob: SingleLangJob) {
  const url = `/api/snc/multilang-jobs/${multilangJob.id}/singleLangJobs/${singleLangJob.jobId}/downloadFiles`
  const fileName = `${displayedName(multilangJob)}-${singleLangJob.targetLanguage.toLowerCase()}.zip`
  return downloadFile(url, fileName)
}

export async function downloadJobFiles(job: MultilangJob) {
  if (job.singleLangJobs.length > 1) {
    return downloadZipFile(job)
  } else {
    return downloadSingleJobFiles(job, job.singleLangJobs[0])
  }
}

export async function downloadQuotePdf(job: MultilangJob) {
  const url = `/api/snc/multilang-jobs/${job.id}/quote.pdf`
  const fileName = `QUOTE_${job.id}.pdf`
  return downloadFile(url, fileName)
}

export interface InputFile {
  fileId: string
  name: string
}

export const getInputFiles = (multilangJobId: string) =>
  axios.get<InputFile[]>(`/api/snc/multilang-jobs/${multilangJobId}/inputFiles`).then((response) => response.data)

export const stripCategoryPrefix = (text: string) => text.replace("Category:", "")

export function getColorForJob(job: MultilangJob): CardColor {
  if (["FINISHED"].includes(job.status)) return "electricMint"
  if (["CANCELLED"].includes(job.status)) return "gray"
  if (["REJECTED"].includes(job.status)) return "pastelRed"
  if (["QUOTED", "QUOTING"].includes(job.status)) return "lightGold"
  if (["FIXED", "FIXED_ASSET_BASED"].includes(job.pricingType)) return "midnightBlue"
  return "primary"
}
